import { NotificationsBell } from "@/features/assistant/NotificationsBell";
import { useQueryClient } from "@tanstack/react-query";
import { Focus, Menu, MessageSquare, Moon, PanelLeftOpen, Plus, Search, Sparkles, Sun, Monitor, Link2, UserRound } from "lucide-react";
import { cn } from "@/lib/utils";
import { post } from "@/lib/api";
import { useUi, type PageName, type Tab, type RightPanel } from "@/lib/store";
import { useNote, useWorkspaces } from "@/lib/queries";
import type { User, Workspace } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { LockOpen } from "lucide-react";
import { lockAll, useKeyring } from "@/features/crypto/keyring";

function Breadcrumbs({ tab, workspace, page }: { tab: Tab | null; workspace?: Workspace; page?: PageName }) {
  const note = useNote(tab?.kind === "note" ? tab.id : undefined);
  const workspaces = useWorkspaces();
  const crumbs: string[] = [];
  if (page) {
    crumbs.push(page === "settings" ? "Settings" : page === "archive" ? "Archive" : `${page === "backlog" ? "Backlog" : page === "files" ? "Files" : "Weekly review"} — all notebooks and projects`);
  } else if (tab?.kind === "note" && note.data) {
    crumbs.push(note.data.workspace.name);
    for (const f of note.data.folderPath) crumbs.push(f.name);
    crumbs.push(note.data.note.title);
  } else if (tab) {
    const ws = workspaces.data?.find((w) => w.id === tab.workspaceId);
    if (ws) crumbs.push(ws.name);
    crumbs.push(tab.title);
  } else if (workspace) {
    crumbs.push(workspace.name);
  }
  return (
    <nav aria-label="Breadcrumb" className="min-w-0 flex-1">
      <ol className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground">
        {crumbs.map((c, i) => (
          <li key={i} className={cn("flex min-w-0 items-center gap-1", i === crumbs.length - 1 ? "font-medium text-foreground" : "hidden sm:flex")}>
            {i > 0 && (
              <span aria-hidden className="hidden sm:inline">
                ›
              </span>
            )}
            <span className="truncate" aria-current={i === crumbs.length - 1 ? "page" : undefined}>
              {c}
            </span>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function TopBar(props: { user: User; activeTab: Tab | null; page?: PageName; narrow: boolean; workspace?: Workspace; onSearch(): void; onNewNote(): void }) {
  const focusMode = useUi((s) => s.focusMode);
  const setFocusMode = useUi((s) => s.setFocusMode);
  const browserCollapsed = useUi((s) => s.browserCollapsed);
  const toggleBrowser = useUi((s) => s.toggleBrowser);
  const rightPanel = useUi((s) => s.rightPanel);
  const setRightPanel = useUi((s) => s.setRightPanel);
  const theme = useUi((s) => s.theme);
  const setTheme = useUi((s) => s.setTheme);
  const setMobileNav = useUi((s) => s.setMobileNav);
  const qc = useQueryClient();
  const devUsers = useQuery({ queryKey: ["dev-users"], queryFn: () => get<{ users: { id: string; displayName: string }[] }>("/api/dev/users"), retry: false });

  const panelBtn = (p: Exclude<RightPanel, null>, label: string, icon: React.ReactNode) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant={rightPanel === p ? "secondary" : "ghost"}
          size="icon-sm"
          aria-pressed={rightPanel === p}
          aria-label={`${rightPanel === p ? "Hide" : "Show"} ${label}`}
          onClick={() => setRightPanel(rightPanel === p ? null : p)}
        >
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );

  return (
    <header className="flex h-12 shrink-0 items-center gap-1.5 border-b bg-background px-2 sm:px-3">
      {props.narrow ? (
        <Button variant="ghost" size="icon-sm" aria-label="Open navigation" onClick={() => setMobileNav(true)}>
          <Menu />
        </Button>
      ) : (
        (browserCollapsed || focusMode) &&
        !props.page && (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={focusMode ? "Leave focus mode" : "Show the notebook or project panel"}
            onClick={() => (focusMode ? setFocusMode(false) : toggleBrowser())}
          >
            <PanelLeftOpen />
          </Button>
        )
      )}
      <Breadcrumbs tab={props.activeTab} workspace={props.workspace} page={props.page} />
      <KeyringIndicator />
      <Button variant="ghost" size="sm" onClick={props.onSearch} aria-label="Search (Ctrl+K)" className="gap-1.5">
        <Search />
        <span className="hidden md:inline">Search</span>
        <kbd className="hidden rounded border px-1 text-[0.65rem] text-muted-foreground md:inline">Ctrl K</kbd>
      </Button>
      <Button size="sm" onClick={props.onNewNote} className="gap-1.5" aria-label="New note (Alt+N)">
        <Plus />
        <span className="hidden sm:inline">New note</span>
      </Button>
      <NotificationsBell />
      <div className="mx-1 hidden h-5 w-px bg-border sm:block" aria-hidden />
      {!props.page && (
        <>
          {panelBtn("sources", "Sources & links", <Link2 />)}
          {panelBtn("comments", "Comments", <MessageSquare />)}
          {panelBtn("assistant", "Assistant", <Sparkles />)}
        </>
      )}
      {!props.narrow && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={focusMode ? "secondary" : "ghost"}
              size="icon-sm"
              aria-pressed={focusMode}
              aria-label={focusMode ? "Leave focus mode" : "Focus mode (hide sidebars)"}
              onClick={() => setFocusMode(!focusMode)}
            >
              <Focus />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Focus mode (Ctrl+Shift+F)</TooltipContent>
        </Tooltip>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label="Theme and identity">
            {theme === "dark" ? <Moon /> : theme === "light" ? <Sun /> : <Monitor />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>Theme</DropdownMenuLabel>
          <DropdownMenuItem onSelect={() => setTheme("light")}>
            <Sun /> Light {theme === "light" && "✓"}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setTheme("dark")}>
            <Moon /> Dark {theme === "dark" && "✓"}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setTheme("system")}>
            <Monitor /> Follow system {theme === "system" && "✓"}
          </DropdownMenuItem>
          {devUsers.data && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Development identity — not authentication</DropdownMenuLabel>
              {devUsers.data.users.map((u) => (
                <DropdownMenuItem
                  key={u.id}
                  disabled={u.id === props.user.id}
                  onSelect={async () => {
                    // Keys unlocked by one person never stay usable for the next one.
                    lockAll();
                    await post("/api/dev/identity", { userId: u.id });
                    await qc.resetQueries();
                  }}
                >
                  <UserRound /> {u.id === props.user.id ? `Signed in as ${u.displayName}` : `Switch to ${u.displayName}`}
                </DropdownMenuItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}

/** Shown while encryption keys are unlocked in this tab; one click locks everything. */
function KeyringIndicator() {
  const count = useKeyring((s) => s.unlocked.length + (s.personalUnlocked ? 1 : 0));
  if (!count) return null;
  return (
    <Button variant="ghost" size="sm" onClick={lockAll} className="gap-1.5 text-primary" title="Encryption keys (yours, or of encrypted notebooks, projects or notes) are unlocked in this tab. Click to lock them all.">
      <LockOpen aria-hidden /> <span className="hidden sm:inline">Unlocked</span> · Lock
    </Button>
  );
}
